import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const requestedPortal = searchParams.get("portal");
  const portal = ["admin", "it"].includes(requestedPortal)
    ? requestedPortal
    : "customer";
  const loginPath =
    portal === "admin"
      ? "/admin/login"
      : portal === "it"
        ? "/it/login"
        : "/login";
  const portalTitle =
    portal === "admin"
      ? "Admin Console"
      : portal === "it"
        ? "IT Service Desk"
        : "Nityavastra account";

  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If the email is registered, a reset link has been sent.");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        "Unable to process password reset request."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white border border-[#E7E5E4] rounded-lg p-8 shadow-sm">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-serif text-[#1C1917]">
              Forgot Password?
            </h1>

            <p className="mt-3 text-sm text-[#78716C]">
              Enter the registered email for your {portalTitle}. We’ll send
              a secure, single-use link that expires in 30 minutes.
            </p>
          </div>

          {sent ? (
            <div className="space-y-6 text-center">
              <div className="rounded-lg bg-[#FAF3E7] border border-[#E7E5E4] p-5">
                <p className="text-sm text-[#57534E]">
                  Check your email for the password reset link.
                </p>
              </div>

              <Link
                to={loginPath}
                className="inline-block text-sm text-[#7C1F30] hover:underline"
              >
                Back to Sign In
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label>Email</Label>

                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-2 bg-white"
                  data-testid="forgot-password-email"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#7C1F30] hover:bg-[#8D2A3D] text-white rounded-full py-6 uppercase text-xs tracking-[0.2em]"
                data-testid="forgot-password-submit"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>

              <div className="text-center">
                <Link
                  to={loginPath}
                  className="text-xs text-[#7C1F30] hover:underline"
                >
                  Back to Sign In
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
